import { useRef, useState, useEffect } from 'react'
import classnames from 'classnames/bind'
import { find, findIndex, isEmpty } from 'lodash-es'
import { useTranslation, Trans } from 'next-i18next'

// Libs
import { useAppSelector, useAppDispatch } from '@/redux/hooks'
import { setHostConnectUser, setHostDisconnectUser, setRoomHandUpClose, removeMicOnItem } from '@/redux/reducers/socket'
import useListFilter from './hooks/useListFilter'
import { handleGtmLiveInteraction } from '@/methods/handleGtmEvent'

// Components
import HandUpItem from './components/HandUpItem'
import Button from '@/components/common/Button'
import Popup from '@/components/common/Popup'
import Icon from '@/assets/icons'

// Constants
import { GtmLiveInteractionType } from '@/constants/gtmEvent'

// Types
import { MicOnItemType, HandUpItemType } from '@/types/room'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

interface ConnectProps {
  handUpList: HandUpItemType[]
  micOnList: MicOnItemType[]
  participantList: any[]
}

const Connect = (props: ConnectProps) => {
  const { micOnList, handUpList, participantList } = props

  const { t } = useTranslation()

  const alreadyMicPopupRef = useRef<any>(null)
  const connectWaitingPopupRef = useRef<any>(null)

  const dispatch = useAppDispatch()

  const socketData = useAppSelector((state) => state.socket)
  const { memberInfoId } = useAppSelector((state) => state.room.roomData)

  const { roomId, userToken, isShowUserOutPopup } = socketData

  const newList = [...micOnList, ...handUpList]

  const userList: any[] = newList.filter((item, index) => findIndex(newList, { uid: item.uid }) === index)

  const { searchValue, handleSearchValueChange, filterUserList } = useListFilter({ userList })

  const currentUserList = searchValue ? filterUserList : userList

  const isMicOn = micOnList.length >= 1

  const hasData = currentUserList?.length > 0

  const [currentUid, setCurrentUid] = useState('')

  const handleUserConnect = ({ uid, name }: any) => {
    if (isMicOn) {
      // TODO: alreadyMicPopupRef 的 popup 要再補掛斷前一通的功能！
      return alreadyMicPopupRef.current.setIsPopupOpen(true)
    }

    dispatch(setHostConnectUser({ targetToken: uid, roomId, name }))
  }

  const handleRoomHandUpCancel = () => {
    dispatch(setRoomHandUpClose({ canHandUp: false }))

    if (isMicOn) dispatch(setHostDisconnectUser({ uid: userToken }))
  }

  const handleConnectWaiting = () => {
    if (isMicOn) {
      return connectWaitingPopupRef.current.setIsPopupOpen(true)
    }
  }

  useEffect(() => {
    if (isShowUserOutPopup) {
      connectWaitingPopupRef.current.setIsPopupOpen(false)
    }
  }, [isShowUserOutPopup])

  return (
    <div className={cx('connect')}>
      <div className={cx('connect-wrapper')}>
        <div className={cx('connect__search')}>
          <input
            className={cx('connect__search-input')}
            value={searchValue}
            onChange={handleSearchValueChange}
            placeholder={t('LiveRoom_Connect_Search_Placeholder')}
          />

          <div className={cx('connect__search-icon')}>
            <Icon.Search />
          </div>
        </div>

        <div className={cx('connect__info')}>
          <div className={cx('connect__info-icon')}>
            <Icon.Info />
          </div>
          <p className={cx('connect__info-text')}>{t('LiveRoom_Connect_Info')}</p>
        </div>

        <div className={cx('connect__list')}>
          {!hasData && <p>{t('LiveRoom_Connect_Empty')}</p>}

          {hasData &&
            currentUserList.map((item, index) => {
              const isMicOn = find(participantList, { uid: item.uid })

              return (
                <HandUpItem
                  key={`${item.uid}-${index}`}
                  className={cx('connect__list-item')}
                  {...item}
                  index={index}
                  handleUserConnect={handleUserConnect}
                  handleUserDisconnect={({ uid }) => {
                    dispatch(setHostDisconnectUser({ uid }))
                    dispatch(removeMicOnItem({ uid }))
                    handleGtmLiveInteraction({ interactionType: GtmLiveInteractionType.LIVE_DISCONNECT, roomId, hostId: memberInfoId })
                  }}
                  handleConnectWaiting={handleConnectWaiting}
                  isMicOn={isMicOn}
                  setCurrentUid={setCurrentUid}
                />
              )
            })}
        </div>
      </div>

      <div className={cx('connect__off')}>
        <Button className={cx('connect__off-button')} isOutline theme='secondary' size='sm' onClick={handleRoomHandUpCancel}>
          {t('LiveRoom_CloseAll_Button')}
        </Button>
      </div>

      <Popup
        ref={alreadyMicPopupRef}
        title={t('LiveRoom_AlreadyMicPopup_Title')}
        confirmLabel={t('LiveRoom_AlreadyMicPopup_Confirm')}
        onConfirm={() => {
          alreadyMicPopupRef.current.setIsPopupOpen(false)
        }}
        hasCancel={false}
      >
        <div className={cx('popup-already')}>
          <Trans
            i18nKey={'LiveRoom_AlreadyMicPopup_Content'}
            components={{
              p: <p />,
              br: <br />,
            }}
          />
        </div>
      </Popup>

      <Popup
        ref={connectWaitingPopupRef}
        title={t('LiveRoom_ConnectWaitingPopup_Title')}
        confirmLabel={t('LiveRoom_WaitingPopup_Confirm')}
        onConfirm={() => {
          connectWaitingPopupRef.current.setIsPopupOpen(false)
        }}
        hasCancel={true}
        canClose={false}
        isWrapperWithCancel={false}
        onCancel={() => {
          if (!isEmpty(currentUid)) {
            dispatch(setHostDisconnectUser({ uid: currentUid }))
            dispatch(removeMicOnItem({ uid: currentUid }))
            connectWaitingPopupRef.current.setIsPopupOpen(false)
          }
        }}
        cancelLabel={t('LiveRoom_WaitingPopup_Cancel')}
      />
    </div>
  )
}

export default Connect
